import '@shui/core/sh-tooltip';

import { Component, booleanAttribute, input, ChangeDetectionStrategy } from '@angular/core';

export type ShTooltipPlacement =
    | 'top'
    | 'bottom'
    | 'left'
    | 'right'
    | 'top-right'
    | 'top-left'
    | 'bottom-right'
    | 'bottom-left'
    | 'left-up'
    | 'left-down'
    | 'right-up'
    | 'right-down'
    | 'pointer';

export type ShTooltipVariation = 'short' | 'long';

@Component({
    selector: 'sh-tooltip',
    template: '<ng-content />',
    imports: [],
    changeDetection: ChangeDetectionStrategy.Eager,
    host: {
        '[attr.target]': 'target()',
        '[attr.label]': 'label() ?? null',
        '[attr.placement]': 'placement()',
        '[attr.variation]': 'variation()',
        '[attr.icon]': 'icon() ?? null',
        '[attr.override]': 'override() ?? null',
        '[attr.delay]': 'delay() ? "" : null',
        '[attr.error]': 'error() ? "" : null',
        '[attr.visible]': 'visible() ? "" : null',
        '[attr.position-fixed-adjustment]': 'positionFixedAdjustment() ? "" : null'
    }
})
export class ShTooltipComponent {
    /** ID of the element this tooltip is attached to. */
    public readonly target = input.required<string>();

    /** Main tooltip text. */
    public readonly label = input<string | undefined>(undefined);

    /** Tooltip placement relative to the target. */
    public readonly placement = input<ShTooltipPlacement>('top');

    /** `short` for a compact single-line tooltip; `long` for rich content. */
    public readonly variation = input<ShTooltipVariation>('long');

    /** Optional icon displayed alongside the label. */
    public readonly icon = input<string | undefined>(undefined);

    /** Optional override styles for the tooltip. */
    public readonly override = input<string | undefined>(undefined);

    /** Adds a 400 ms hover delay before the tooltip appears. */
    public readonly delay = input(false, { transform: booleanAttribute });

    /** Applies error styling. */
    public readonly error = input(false, { transform: booleanAttribute });

    /** Forces the tooltip to stay visible (testing / guided flows). */
    public readonly visible = input(false, { transform: booleanAttribute });

    /**
     * Set to `true` when a parent uses CSS that changes the containing block
     * for fixed positioning (e.g. `transform`, `will-change`).
     */
    public readonly positionFixedAdjustment = input(false, { transform: booleanAttribute });
}
